import BackendService from 'BackendService';
import { flushClientLogs, startClientLogCapture, stopClientLogCapture } from '../clientLogCapture';
import {
  clearSupportLoggingSession,
  readSupportLoggingSession,
  saveSupportLoggingSession,
  SupportLoggingSession,
} from '../supportLoggingStore';

/**
 * Runs an admin's own "Capture my logs" window (Settings -> Support) for the whole admin app, not
 * just the Settings page: starts it, resumes it after a reload, and turns it off by itself when
 * the backend's 30 minutes are up or the backend stops accepting logs.
 */

type Listener = (session: SupportLoggingSession | null) => void;

const listeners = new Set<Listener>();
let expiryTimer: ReturnType<typeof setTimeout> | null = null;

function notify(session: SupportLoggingSession | null) {
  listeners.forEach((listener) => listener(session));
}

function clearLocal() {
  stopClientLogCapture();
  clearSupportLoggingSession();
  if (expiryTimer) {
    clearTimeout(expiryTimer);
    expiryTimer = null;
  }
  notify(null);
}

function begin(session: SupportLoggingSession) {
  saveSupportLoggingSession(session);
  startClientLogCapture(async (entries) => {
    const accepted = await new BackendService().sendSupportClientLogs(session.token, entries);
    if (!accepted) clearLocal();
    return accepted;
  });
  if (expiryTimer) clearTimeout(expiryTimer);
  expiryTimer = setTimeout(
    () => void endSupportLogging(),
    Math.max(0, session.expiresAt - Date.now()),
  );
  notify(session);
}

export async function startSupportLogging(): Promise<SupportLoggingSession> {
  const session = await new BackendService().startSupportLogging();
  begin(session);
  return session;
}

export async function endSupportLogging(): Promise<void> {
  const session = readSupportLoggingSession();
  await flushClientLogs();
  clearLocal();
  if (session) {
    // Best effort: only leaves an end marker in the log; the token expires on its own anyway.
    await new BackendService().stopSupportLogging(session.token).catch(() => undefined);
  }
}

/** Picks a window that is still running back up after a reload or in a new tab. */
export function resumeSupportLogging(): void {
  const session = readSupportLoggingSession();
  if (session) begin(session);
}

export function subscribeSupportLogging(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
